import { ChangeDetectionStrategy, Component, InputSignal, effect, input } from "@angular/core";
import { NxActionComponent } from "@aposin/ng-aquila/action";
import {
  NxFlatTreeControl,
  NxFlatTreeNode,
  NxTreeComponent,
  NxTreeFlatDataSource,
  NxTreeNode,
  NxTreeNodeActionItem,
  NxTreeNodeComponent,
  NxTreeNodeDefDirective,
  NxTreeNodePaddingDirective,
  NxTreeNodeToggleDirective
} from "@aposin/ng-aquila/tree";

interface MyTreeNode extends NxTreeNode {
  children?: MyTreeNode[];
  label: string;
  url?: string;
}

interface MyFlatTreeNode extends NxFlatTreeNode {
  label: string;
  url?: string;
}

export type ArrayTitleFunction = (obj: any, index: number) => string;

@Component({
  selector: "app-object-viewer",
  standalone: true,
  imports: [
    NxTreeComponent,
    NxTreeNodeDefDirective,
    NxTreeNodeComponent,
    NxActionComponent,
    NxTreeNodeActionItem,
    NxTreeNodePaddingDirective,
    NxTreeNodeToggleDirective
  ],
  templateUrl: "./object-viewer.component.html",
  styleUrl: "./object-viewer.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObjectViewerComponent {
  readonly object: InputSignal<any> = input<any>();
  readonly getArrayTitle: InputSignal<ArrayTitleFunction> = input<ArrayTitleFunction>();

  _treeControl: NxFlatTreeControl<MyFlatTreeNode> = new NxFlatTreeControl();
  _hasChild: Function = (_: number, node: NxFlatTreeNode) => node.expandable;
  _dataSource: NxTreeFlatDataSource<MyTreeNode, MyFlatTreeNode> = new NxTreeFlatDataSource(
    this._treeControl,
    this.dataToTreeNode(this.object())
  );

  constructor() {
    effect(() => {
      this.object();
      this.updateDatas();
    });
  }

  /**
   * met à jour l'objet
   */
  updateDatas() {
    const nodes: MyTreeNode[] = this.dataToTreeNode(this.object());
    this._dataSource = new NxTreeFlatDataSource(this._treeControl, nodes);
  }

  /**
   * formatte l'objet ou le tableau pour le composant nxTree
   * @param data objet à formatter
   * @returns ensemble de treenodes
   */
  dataToTreeNode(data: any): MyTreeNode[] {
    if (Array.isArray(data)) {
      return this.arrayNodeToTreeNodes(data);
    }
    return this.objNodeToTreeNodes(data);
  }

  /**
   * formatte l'objet pour le composant nxTree
   * @param data objet à formatter
   * @returns ensemble de treenodes
   */
  objNodeToTreeNodes(obj: any): MyTreeNode[] {
    if (!obj) {
      return [];
    }
    if (!this.isObjet(obj)) {
      return [];
    }

    const treeNodes: MyTreeNode[] = [];
    for (const key in obj) {
      const treeNode: MyTreeNode = this.fillTreeNode(key, obj[key]);
      if (treeNode) treeNodes.push(treeNode);
    }
    return treeNodes;
  }

  /**
   * remplit le treenode
   * @param label label
   * @param content contenu
   * @returns treenode
   */
  fillTreeNode(label: string, content: any): MyTreeNode {
    const treeNode: MyTreeNode = {
      label
    };
    if (this.isObjet(content)) {
      const subNodes: MyTreeNode[] = this.dataToTreeNode(content);
      if (subNodes.length === 0) return null;
      treeNode.children = subNodes;
    } else {
      if (content?.includes?.("https://")) {
        treeNode.url = content;
      }
      const contentDesc: string = content?.toString() ?? "[vide]";
      if (!contentDesc) {
        return null;
      }
      treeNode.label += " : " + contentDesc;
    }
    return treeNode;
  }

  /**
   * formatte le tableau pour le composant nxTree
   * @param arr tableau
   * @returns ensemble de treenodes
   */
  arrayNodeToTreeNodes(arr: any[]): MyTreeNode[] {
    if (!arr) {
      return [];
    }
    if (!Array.isArray(arr)) {
      return [];
    }

    return arr
      .map((obj, index) => {
        let label: string = index?.toString();
        const getArrayTitle: ArrayTitleFunction = this.getArrayTitle();
        if (getArrayTitle) {
          label = getArrayTitle(obj, index);
        }
        return this.fillTreeNode(label, obj);
      })
      .filter(Boolean);
  }

  /**
   * vérifie si la valeur a des sous-valeurs
   * @param value valeur
   * @returns true si contient des sous-valeurs
   */
  isObjet(value: any) {
    if (!value) return false;
    return typeof value === "object";
  }
}
